import {
	CaretRight,
	Drop,
	Fire,
	HouseLine,
	Lightning,
	Thermometer,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { NavLink, useParams } from "react-router";
import type { TenantApartment } from "@/api/tenant/types";
import { Card } from "@/components/ui/card";
import { isValidLang, ROUTE_SLUGS } from "@/config/routes";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/utils/format-datetime";

interface ApartmentCardProps {
	apartment: TenantApartment;
}

type MeterBucket = "water" | "heat" | "electricity" | "other";

function bucketFor(type: string): MeterBucket {
	const t = type.toLowerCase();
	if (t.includes("water")) return "water";
	if (t.includes("heat")) return "heat";
	if (t.includes("electric")) return "electricity";
	return "other";
}

function getLatestCommunication(
	meters: TenantApartment["meters"],
): string | null {
	const dates = meters
		.map((m) => m.last_communication)
		.filter((d): d is string => d !== null);
	if (dates.length === 0) return null;
	return dates.sort().at(-1) ?? null;
}

type FreshnessKey = "fresh" | "recent" | "stale" | "never";

function freshnessOf(iso: string | null): FreshnessKey {
	if (!iso) return "never";
	const ageMs = Date.now() - new Date(iso).getTime();
	const day = 24 * 60 * 60 * 1000;
	if (ageMs <= 2 * day) return "fresh";
	if (ageMs <= 14 * day) return "recent";
	return "stale";
}

export function ApartmentCard({ apartment }: ApartmentCardProps) {
	const { t } = useTranslation();
	const { lang } = useParams<{ lang: string }>();
	const currentLang = lang && isValidLang(lang) ? lang : "pl";
	const last = getLatestCommunication(apartment.meters);
	const href = `/${currentLang}/${ROUTE_SLUGS.myApartments[currentLang]}/${apartment.id}`;

	const counts = apartment.meters.reduce<Record<MeterBucket, number>>(
		(acc, m) => {
			acc[bucketFor(m.type)] += 1;
			return acc;
		},
		{ water: 0, heat: 0, electricity: 0, other: 0 },
	);

	type Breakdown = {
		key: MeterBucket;
		icon: React.ReactNode;
		label: string;
		count: number;
		tone: string;
	};
	const breakdown: Breakdown[] = [
		{
			key: "water",
			icon: <Drop weight="fill" className="size-3.5" />,
			label: t("tenant.list.meterBreakdown.water"),
			count: counts.water,
			tone: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
		},
		{
			key: "heat",
			icon: <Fire weight="fill" className="size-3.5" />,
			label: t("tenant.list.meterBreakdown.heat"),
			count: counts.heat,
			tone: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
		},
		{
			key: "electricity",
			icon: <Lightning weight="fill" className="size-3.5" />,
			label: t("tenant.list.meterBreakdown.electricity"),
			count: counts.electricity,
			tone: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
		},
		{
			key: "other",
			icon: <Thermometer weight="fill" className="size-3.5" />,
			label: t("tenant.list.meterBreakdown.other"),
			count: counts.other,
			tone: "bg-muted text-muted-foreground",
		},
	];
	const visibleBreakdown = breakdown.filter((b) => b.count > 0);

	const freshness = freshnessOf(last);
	const statusTone: Record<FreshnessKey, string> = {
		fresh: "bg-emerald-500",
		recent: "bg-sky-500",
		stale: "bg-amber-500",
		never: "bg-muted-foreground/40",
	};
	const statusLabel: Record<FreshnessKey, string> = {
		fresh: t("tenant.list.statusFresh"),
		recent: t("tenant.list.statusRecent"),
		stale: t("tenant.list.statusStale"),
		never: t("tenant.list.statusNever"),
	};

	return (
		<NavLink
			to={href}
			className="group block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
			aria-label={`${apartment.street} ${apartment.apartment_number}`}
		>
			<Card className="relative overflow-hidden border-border/60 px-5 py-5 transition-all duration-200 group-hover:border-primary/30 group-hover:shadow-md sm:px-6 sm:py-6">
				<div className="pointer-events-none absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-primary/[0.04] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

				<div className="relative flex items-stretch gap-4 sm:gap-6">
					<div className="flex shrink-0 items-center">
						<div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary sm:size-14">
							<HouseLine weight="duotone" className="size-6 sm:size-7" />
						</div>
					</div>

					<div className="flex min-w-0 flex-1 flex-col justify-center gap-2.5">
						<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
							<h2 className="truncate text-lg font-semibold tracking-tight sm:text-xl">
								{apartment.street}
							</h2>
							<span className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs tabular-nums text-muted-foreground">
								{apartment.apartment_number}
							</span>
						</div>

						{visibleBreakdown.length > 0 ? (
							<div className="flex flex-wrap items-center gap-1.5">
								{visibleBreakdown.map((b) => (
									<span
										key={b.key}
										className={cn(
											"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
											b.tone,
										)}
										title={b.label}
									>
										{b.icon}
										<span className="tabular-nums">{b.count}</span>
										<span className="hidden sm:inline">{b.label}</span>
									</span>
								))}
								<span className="text-xs text-muted-foreground">
									·{" "}
									{t("tenant.list.metersCount", {
										count: apartment.meters.length,
									})}
								</span>
							</div>
						) : (
							<p className="text-sm text-muted-foreground">
								{t("tenant.apartment.noMeters")}
							</p>
						)}
					</div>

					<div className="hidden shrink-0 flex-col items-end justify-center gap-1.5 text-right sm:flex">
						<div className="flex items-center gap-2">
							<span
								className={cn(
									"size-2 rounded-full",
									statusTone[freshness],
									freshness === "fresh" && "animate-pulse",
								)}
								aria-hidden="true"
							/>
							<span className="text-xs font-medium text-foreground/80">
								{statusLabel[freshness]}
							</span>
						</div>
						<p className="text-xs tabular-nums text-muted-foreground">
							{last
								? formatDateTime(last, currentLang)
								: t("tenant.list.neverCommunicated")}
						</p>
					</div>

					<div className="flex shrink-0 items-center text-muted-foreground transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-primary">
						<CaretRight className="size-5" />
					</div>
				</div>

				<div className="relative mt-3 flex items-center justify-between border-t border-border/50 pt-3 text-xs sm:hidden">
					<div className="flex items-center gap-2">
						<span
							className={cn("size-2 rounded-full", statusTone[freshness])}
							aria-hidden="true"
						/>
						<span className="font-medium text-foreground/80">
							{statusLabel[freshness]}
						</span>
					</div>
					<span className="tabular-nums text-muted-foreground">
						{last
							? formatDateTime(last, currentLang)
							: t("tenant.list.neverCommunicated")}
					</span>
				</div>
			</Card>
		</NavLink>
	);
}
